import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { Plus, Clock, CheckCircle, XCircle, FileText, TrendingUp } from 'lucide-react';

export default function ClientRFPManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rfps, setRfps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRFPs();
  }, []);

  const fetchRFPs = async () => {
    try {
      const response = await api.getRFPs();
      setRfps(response.data);
    } catch (error) {
      toast.error('Failed to fetch RFPs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { color: 'bg-green-100 text-green-800', icon: Clock, label: 'Open - Accepting Bids' },
      closed: { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const viewBids = (rfpId) => {
    navigate(`/client/rfps/${rfpId}/bids`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto mb-4"></div>
          <p className="text-slate-800">Loading your RFPs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-navy-800">My RFPs & Bidding</h1>
            <p className="text-slate-800 mt-2 text-sm sm:text-base">Manage requests for proposals and compare vendor bids</p>
          </div>
          <Button
            onClick={() => navigate('/client/rfps/create')}
            className="bg-navy-600 hover:bg-navy-700 w-full sm:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create New RFP
          </Button>
        </div>

        {rfps.length === 0 ? (
          <Card className="border-2 border-dashed border-navy-300">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-navy-300 mb-4" />
              <h3 className="text-xl font-semibold text-navy-800 mb-2">
                No RFPs Yet
              </h3>
              <p className="text-slate-800 mb-6 text-center max-w-md">
                Create your first RFP to get competitive quotes from multiple vendors
              </p>
              <Button
                onClick={() => navigate('/client/rfps/create')}
                className="bg-navy-600 hover:bg-navy-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First RFP
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {rfps.map((rfp) => (
              <Card key={rfp.id} className="border-2 border-navy-200 hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <CardTitle className="text-lg sm:text-xl text-navy-800">
                          {rfp.event_name}
                        </CardTitle>
                        {getStatusBadge(rfp.status)}
                      </div>
                      <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-slate-800">
                        <span>🎯 {rfp.event_type}</span>
                        <span>📍 {rfp.location}</span>
                        <span>👥 {rfp.attendees} attendees</span>
                        <span className="hidden sm:inline">📅 {new Date(rfp.start_date).toLocaleDateString()} - {new Date(rfp.end_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl sm:text-2xl font-bold text-navy-800">
                        {rfp.quotes_received}
                      </div>
                      <div className="text-xs sm:text-sm text-slate-800">Bids Received</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="text-sm font-medium text-slate-900 mb-1">Budget Range:</div>
                    <div className="text-slate-800">{rfp.budget_range}</div>
                  </div>
                  <div className="mb-4">
                    <div className="text-sm font-medium text-slate-900 mb-1">Requirements:</div>
                    <div className="text-sm text-slate-800 line-clamp-2">{rfp.requirements}</div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => viewBids(rfp.id)}
                      className="flex-1 bg-navy-600 hover:bg-navy-700"
                      disabled={rfp.quotes_received === 0}
                    >
                      <TrendingUp className="mr-2 h-4 w-4" />
                      {rfp.quotes_received === 0 ? 'Waiting for Bids' : `View ${rfp.quotes_received} Bids`}
                    </Button>
                  </div>
                  <div className="mt-3 text-xs text-slate-700">
                    Created: {new Date(rfp.created_at).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}